"use client";

import type {
  BarGeometry,
  DensityGeometry,
  Frame,
  LineGeometry,
  Tick,
} from "@/lib/tool-kit/stats-distributions/chart";
import {
  exponentText,
  type NumberFormatter,
  needsScientific,
  scientificParts,
} from "@/lib/tool-kit/stats-distributions/format";

/*
 * Drawing for the `stats-distributions` family: the shaded density sketch, the bar
 * chart of a probability mass function, and the power curve. Geometry comes from
 * `@/lib/tool-kit/stats-distributions/chart`; every label comes in through props from
 * the calling widget's own strings, so nothing here is English.
 *
 * Colours are theme utilities rather than hex values: these sketches are read on the
 * page, in light or dark mode, not exported.
 */

/** A tick label's horizontal position as a percentage of the frame. */
function percentX(position: number, frame: Frame): string {
  return `${(position / frame.width) * 100}%`;
}

function percentY(position: number, frame: Frame): string {
  return `${(position / frame.height) * 100}%`;
}

/** A row of HTML labels under a chart, each centred under its tick. */
function AxisLabels({
  ticks,
  frame,
  format,
}: {
  ticks: readonly Tick[];
  frame: Frame;
  format: (value: number) => string;
}) {
  return (
    <div
      aria-hidden="true"
      className="relative h-5 text-muted-foreground text-xs"
    >
      {ticks.map((tick) => (
        <span
          className="absolute top-0 -translate-x-1/2 whitespace-nowrap tabular-nums"
          key={tick.value}
          style={{ left: percentX(tick.position, frame) }}
        >
          {format(tick.value)}
        </span>
      ))}
    </div>
  );
}

/** A probability: four decimals, or m × 10ⁿ once four decimals would read 0.0000. */
export function Probability({
  value,
  fmt,
  digits = 4,
}: {
  value: number;
  fmt: NumberFormatter;
  digits?: number;
}) {
  if (needsScientific(value)) {
    const { mantissa, exponent } = scientificParts(value);
    return (
      <span className="whitespace-nowrap">
        {fmt(mantissa, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
        × 10
        <sup>{exponentText(exponent, fmt)}</sup>
      </span>
    );
  }
  return (
    <>
      {fmt(value, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}
    </>
  );
}

export function DensityChart({
  geometry,
  label,
  format,
}: {
  geometry: DensityGeometry;
  /** Read by screen readers in place of the picture. */
  label: string;
  format: (value: number) => string;
}) {
  const { frame, baseline } = geometry;
  return (
    <div>
      <svg
        aria-label={label}
        className="block h-auto w-full"
        role="img"
        viewBox={`0 0 ${frame.width} ${frame.height}`}
      >
        {geometry.areas.map((d) => (
          <path className="fill-primary/25" d={d} key={d} />
        ))}
        <path
          className="stroke-primary"
          d={geometry.curve}
          fill="none"
          strokeLinejoin="round"
          strokeWidth={2.5}
        />
        <line
          className="stroke-muted-foreground"
          strokeWidth={1}
          x1={frame.left}
          x2={frame.width - frame.right}
          y1={baseline}
          y2={baseline}
        />
        {geometry.ticks.map((tick) => (
          <line
            className="stroke-muted-foreground"
            key={tick.value}
            x1={tick.position}
            x2={tick.position}
            y1={baseline}
            y2={baseline + 5}
          />
        ))}
        {geometry.markers.map((marker, index) => (
          <line
            className="stroke-foreground"
            // Markers can coincide (a = b), so position alone is not a key.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional markers
            key={index}
            strokeDasharray={marker.offScale ? "2 4" : "5 4"}
            strokeWidth={1.5}
            x1={marker.position}
            x2={marker.position}
            y1={frame.top}
            y2={baseline}
          />
        ))}
      </svg>
      <AxisLabels format={format} frame={frame} ticks={geometry.ticks} />
    </div>
  );
}

export function BarChart({
  geometry,
  label,
  highlighted,
  format,
}: {
  geometry: BarGeometry;
  label: string;
  /** Bars drawn in the solid colour: the k values the reader's probability covers. */
  highlighted: (k: number) => boolean;
  format: (value: number) => string;
}) {
  const { frame, baseline } = geometry;
  return (
    <div>
      <svg
        aria-label={label}
        className="block h-auto w-full"
        role="img"
        viewBox={`0 0 ${frame.width} ${frame.height}`}
      >
        {geometry.bars.map((bar) => (
          <rect
            className={highlighted(bar.k) ? "fill-primary" : "fill-primary/25"}
            height={bar.height}
            key={bar.k}
            width={bar.width}
            x={bar.x}
            y={bar.y}
          />
        ))}
        <line
          className="stroke-muted-foreground"
          strokeWidth={1}
          x1={frame.left}
          x2={frame.width - frame.right}
          y1={baseline}
          y2={baseline}
        />
      </svg>
      <AxisLabels format={format} frame={frame} ticks={geometry.ticks} />
    </div>
  );
}

export function PowerChart({
  geometry,
  label,
  xLabel,
  yLabel,
  target,
  marker,
  format,
}: {
  geometry: LineGeometry;
  label: string;
  xLabel: string;
  yLabel: string;
  /** The power the reader is aiming for, drawn as a dashed line. */
  target: number | null;
  /** The answer: (n, power). */
  marker: { n: number; power: number } | null;
  format: (value: number) => string;
}) {
  const { frame, baseline, project } = geometry;
  const right = frame.width - frame.right;
  const targetY = target === null ? null : project(0, target).y;
  const dot = marker === null ? null : project(marker.n, marker.power);
  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs">{yLabel}</p>
      <div className="grid grid-cols-[auto_1fr] gap-x-2">
        <div
          aria-hidden="true"
          className="relative w-7 text-right text-muted-foreground text-xs tabular-nums"
        >
          {geometry.yTicks.map((tick) => (
            <span
              className="absolute right-0 -translate-y-1/2"
              key={tick.value}
              style={{ top: percentY(tick.position, frame) }}
            >
              {format(tick.value)}
            </span>
          ))}
        </div>
        <svg
          aria-label={label}
          className="block h-auto w-full"
          role="img"
          viewBox={`0 0 ${frame.width} ${frame.height}`}
        >
          {geometry.yTicks.map((tick) => (
            <line
              className="stroke-border"
              key={tick.value}
              x1={frame.left}
              x2={right}
              y1={tick.position}
              y2={tick.position}
            />
          ))}
          {targetY === null ? null : (
            <line
              className="stroke-muted-foreground"
              strokeDasharray="6 4"
              x1={frame.left}
              x2={right}
              y1={targetY}
              y2={targetY}
            />
          )}
          <path
            className="stroke-primary"
            d={geometry.path}
            fill="none"
            strokeLinejoin="round"
            strokeWidth={2.5}
          />
          {dot === null ? null : (
            <circle className="fill-primary" cx={dot.x} cy={dot.y} r={5} />
          )}
          <line
            className="stroke-muted-foreground"
            x1={frame.left}
            x2={right}
            y1={baseline}
            y2={baseline}
          />
        </svg>
        <span />
        <AxisLabels format={format} frame={frame} ticks={geometry.xTicks} />
        <span />
        <p className="text-center text-muted-foreground text-xs">{xLabel}</p>
      </div>
    </div>
  );
}
